// ...import block
import {
  doc,
  setDoc,
  deleteDoc,
  getDoc,
  collection,
  getDocs,
  updateDoc,
  arrayUnion,
  arrayRemove,
  addDoc,
} from "firebase/firestore";

import { db } from "../firebase";
import { useAuth } from "../AuthContext";
import { useToast } from "../ToastContext";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";

export default function Technique({ technique, isExpanded, onExpand }) {
  const user = useAuth();
  const { addToast } = useToast();
  const [isFavourite, setIsFavourite] = useState(false);
  const [userCollections, setUserCollections] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [collectionSearchQuery, setCollectionSearchQuery] = useState("");
  const [newCollectionName, setNewCollectionName] = useState("");
  const dropdownRef = useRef(null);
  const toggleButtonRef = useRef(null);
  const excerptLimit = 240;

  const {
    name,
    author,
    platform,
    description,
    url,
    image,
    thumbnail,
    previewUrl,
    linkLabel,
    secondaryUrl,
    secondaryLabel,
    series,
  } = technique;
  const unifiedDate =
    technique.date || technique.publishedAt || technique.scrapedAt;
  const publishedDate = technique.publishedAt;
  const scrapedDate = technique.scrapedAt;
  const updatedDate = technique.lastUpdatedAt;

  const formatUnifiedDate = (value) => {
    if (!value) {
      return null;
    }
    const parsed = Date.parse(value);
    if (Number.isNaN(parsed)) {
      return value;
    }
    return new Date(parsed).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const formatMetaDate = (value) => {
    if (!value) {
      return null;
    }
    const parsed = Date.parse(value);
    if (Number.isNaN(parsed)) {
      return value;
    }
    return new Date(parsed).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formattedUnifiedDate = formatUnifiedDate(unifiedDate);
  const formattedPublishedDate = formatMetaDate(publishedDate);
  const formattedScrapedDate = formatMetaDate(scrapedDate);
  const formattedUpdatedDate = formatMetaDate(updatedDate);

  const safeId = encodeURIComponent(
    technique.url || technique.videoUrl || technique.name,
  );
  const imageSrc = thumbnail || (image ? `./meta_images/${image}` : null);
  const showPreviewImage = platform !== "Patreon" && Boolean(imageSrc);
  const previewHref = previewUrl || url;
  const primaryLinkLabel = linkLabel || `View on ${platform}`;
  const clippedDescription =
    description && description.length > excerptLimit
      ? `${description.slice(0, excerptLimit).trim()}...`
      : description;

  const fetchCollections = useCallback(async () => {
    if (!user) return;
    const snapshot = await getDocs(
      collection(db, "users", user.uid, "collections"),
    );
    const data = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    setUserCollections(data);
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const checkIfFavourite = async () => {
      const favRef = doc(db, "users", user.uid, "favourites", safeId);
      const favSnap = await getDoc(favRef);
      setIsFavourite(favSnap.exists());
    };

    checkIfFavourite();
    fetchCollections();
  }, [user, safeId, fetchCollections]);

  const toggleFavourite = async () => {
    if (!user) return addToast("Please log in to save favourites", "error");

    const favRef = doc(db, "users", user.uid, "favourites", safeId);
    try {
      if (isFavourite) {
        await deleteDoc(favRef);
        setIsFavourite(false);
        addToast("Removed from favourites", "success");
      } else {
        await setDoc(favRef, technique);
        setIsFavourite(true);
        addToast("Added to favourites", "success");
      }
    } catch (error) {
      addToast("Failed to update favourite", "error");
    }
  };

  const toggleInCollection = async (collectionId, isIncluded) => {
    if (!user) return;

    try {
      const ref = doc(db, "users", user.uid, "collections", collectionId);
      await updateDoc(ref, {
        tutorials: isIncluded
          ? arrayRemove(technique.url)
          : arrayUnion(technique.url),
      });

      const updated = userCollections.map((col) => {
        if (col.id !== collectionId) return col;
        const updatedTutorials = isIncluded
          ? col.tutorials.filter((url) => url !== technique.url)
          : [...(col.tutorials || []), technique.url];
        return { ...col, tutorials: updatedTutorials };
      });

      setUserCollections(updated);
      addToast(
        isIncluded ? "Removed from collection" : "Added to collection",
        "success",
      );
    } catch (error) {
      addToast("Failed to update collection", "error");
    }
  };

  const recordClick = async (clickUrl) => {
    if (!user) return;
    try {
      await addDoc(collection(db, "users", user.uid, "clicks"), {
        title: name,
        platform,
        url: clickUrl,
        timestamp: new Date().toISOString(),
      });
    } catch (e) {
      // click tracking is non-critical
    }
  };

  const createCollectionAndAdd = async (name) => {
    if (!user) return;
    try {
      await addDoc(collection(db, "users", user.uid, "collections"), {
        name,
        description: "",
        tutorials: [technique.url],
      });
      setNewCollectionName("");
      fetchCollections();
      addToast(`Created "${name}" and added technique`, "success");
      setShowDropdown(false);
    } catch (error) {
      addToast("Failed to create collection", "error");
    }
  };

  const availableCollections = useMemo(() => {
    return userCollections
      .filter((col) => !col.tutorials?.includes(technique.url))
      .sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, {
          sensitivity: "base",
        }),
      );
  }, [technique.url, userCollections]);

  const filteredAvailableCollections = useMemo(() => {
    const query = collectionSearchQuery.trim().toLowerCase();
    if (!query) {
      return availableCollections;
    }
    return availableCollections.filter((col) =>
      String(col.name || "")
        .toLowerCase()
        .includes(query),
    );
  }, [availableCollections, collectionSearchQuery]);

  useEffect(() => {
    if (!showDropdown) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      const clickedToggle = toggleButtonRef.current?.contains(event.target);
      const clickedDropdown = dropdownRef.current?.contains(event.target);
      if (!clickedToggle && !clickedDropdown) {
        setShowDropdown(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [showDropdown]);

  useEffect(() => {
    if (!showDropdown) {
      setCollectionSearchQuery("");
    }
  }, [showDropdown]);

  return (
    <div className="technique-item" onClick={() => onExpand(technique.url)}>
      <div className="technique-top">
        <h2 className="technique-title">{name}</h2>
        <h3 className="technique-author">{author !== "Unknown" && author}</h3>
        {showPreviewImage && (
          <a href={previewHref} target="_blank" rel="noopener noreferrer">
            <img
              src={imageSrc}
              alt={name}
              className="preview-image"
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
          </a>
        )}
      </div>

      <div className="technique-content">
        {clippedDescription && (
          <p className="technique-description">{clippedDescription}</p>
        )}
        {Array.isArray(series) && series.length > 0 && (
          <div className="technique-series-row">
            {series.map((label) => (
              <span key={label} className="technique-series-chip">
                {label}
              </span>
            ))}
          </div>
        )}
        {(formattedUnifiedDate ||
          formattedPublishedDate ||
          formattedScrapedDate ||
          formattedUpdatedDate) && (
          <div className="technique-dates-block">
            {formattedUnifiedDate && (
              <p className="technique-unified-date">{formattedUnifiedDate}</p>
            )}
            {(formattedPublishedDate ||
              formattedScrapedDate ||
              formattedUpdatedDate) && (
              <p className="technique-meta-date">
                {[
                  formattedPublishedDate && `P: ${formattedPublishedDate}`,
                  formattedScrapedDate && `S: ${formattedScrapedDate}`,
                  formattedUpdatedDate && `E: ${formattedUpdatedDate}`,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="buttons">
        <div className="collection-wrapper">
          <div className="collection-badge-group">
            {userCollections
              .filter((col) => col.tutorials?.includes(technique.url))
              .map((col) => (
                <div key={col.id} className="collection-badge">
                  {col.name}
                  <button
                    className="remove-badge"
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleInCollection(col.id, true);
                    }}
                  >
                    &times;
                  </button>
                </div>
              ))}
            <button
              ref={toggleButtonRef}
              className="collection-add-button"
              onClick={() => {
                if (!showDropdown && toggleButtonRef.current) {
                  const rect = toggleButtonRef.current.getBoundingClientRect();
                  setDropdownPosition({
                    top: rect.top + window.scrollY,
                    left: rect.left + window.scrollX + rect.width / 2,
                  });
                }
                setShowDropdown((prev) => !prev);
              }}
            >
              + Collection
            </button>
          </div>
          {showDropdown && createPortal(
            <div
              ref={dropdownRef}
              className="collection-dropdown"
              role="dialog"
              aria-label="Add to collection"
              style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
            >
              <div className="collection-dropdown-header">
                <div className="collection-dropdown-title">
                  Add to collection
                </div>
                <div className="collection-dropdown-count">
                  {availableCollections.length} available
                </div>
              </div>
              {availableCollections.length > 6 && (
                <input
                  className="collection-search-input"
                  type="text"
                  value={collectionSearchQuery}
                  onChange={(event) =>
                    setCollectionSearchQuery(event.target.value)
                  }
                  placeholder="Search collections"
                  aria-label="Search collections"
                />
              )}
              <div className="collection-dropdown-list">
                {filteredAvailableCollections.length === 0 ? (
                  <div className="collection-dropdown-empty">
                    No matching collections.
                  </div>
                ) : (
                  filteredAvailableCollections.map((col) => (
                    <div
                      key={col.id}
                      className="dropdown-item"
                      onClick={() => {
                        toggleInCollection(col.id, false);
                        setShowDropdown(false);
                      }}
                    >
                      {col.name}
                    </div>
                  ))
                )}
              </div>
              <div className="create-collection">
                <input
                  type="text"
                  placeholder="New collection name"
                  value={newCollectionName}
                  onChange={(e) => setNewCollectionName(e.target.value)}
                  style={{ marginBottom: 10 }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && newCollectionName.trim()) {
                      createCollectionAndAdd(newCollectionName.trim());
                      setShowDropdown(false);
                    }
                  }}
                />
                <button
                  style={{
                    width: '100%',
                    padding: '10px 0',
                    margin: '0 auto',
                    background: 'var(--accent)',
                    color: '#181a20',
                    fontWeight: 600,
                    fontSize: '1.15rem',
                    border: 'none',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                    transition: 'background 0.2s',
                  }}
                  disabled={!newCollectionName.trim()}
                  onClick={() => {
                    if (newCollectionName.trim()) {
                      createCollectionAndAdd(newCollectionName.trim());
                      setShowDropdown(false);
                    }
                  }}
                >
                  Create Collection
                </button>
              </div>
            </div>
          , document.body)}
        </div>

        <div className="action-row">
          {user && (
            <button className="favourite-button" onClick={toggleFavourite}>
              {isFavourite ? "★" : "☆"}
            </button>
          )}
          <a
            className="learn-more-btn"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => recordClick(url)}
          >
            {primaryLinkLabel}
          </a>
          {secondaryUrl && (
            <a
              className="learn-more-btn secondary"
              href={secondaryUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => recordClick(secondaryUrl)}
            >
              {secondaryLabel || "Open Video"}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
